import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Grid,
  LinearProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Compare as CompareIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Add as AddIcon,
  MoreHoriz as MoreHorizIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  MenuBook as MenuBookIcon,
  Circle as CircleIcon
} from '@mui/icons-material';
import { uploadFilePath, fileServerUrl, filePreviewServerUrl } from '../../../config';


import * as gprojectActions from "../../../store/gproject";
import * as g04docuGTestComparisonActions from "../../../store/g04docuGTestComparison";
import DocumentViewerDrawer from '../../common/viewer/DocumentViewerDrawer';
import SubstituteDocumentDialog from './SubstituteDocumentDialog';
import FolderSubstitutePickerDialog from './FolderSubstitutePickerDialog';
import TestComparisonDialog from './TestComparisonDialog';
import { getDivisionLabel } from '../../../utils/divisionUtils';
import DocumentStatusLegend, { DOC_STATUS_COLOR, substituteIconColor } from '../documentStatusLegend';

/** Windows 경로의 \\ 를 / 로 맞춘 뒤 업로드 루트를 잘라 미리보기 주소로 만든다. */
const toFilePreviewUrl = (documentPath, ownFile) => {
  const normalized = String(documentPath).replace(/\\/g, '/');
  const root = String(uploadFilePath || '').replace(/\\/g, '/');
  if (ownFile) {
    const randNumber = Math.floor(Math.random() * 99);
    const encodedPath = normalized.replace(root, '').split('/')
      .map(part => encodeURIComponent(part))
      .join('/');
    return `${fileServerUrl}${encodedPath}?q=cat&${randNumber}`;
  }
  const encodedPath = normalized.split('/')
    .map(part => encodeURIComponent(part))
    .join('/');
  return `${filePreviewServerUrl}${encodedPath}`;
};

// 팝업 메뉴에서 제품명과 회사명을 분리하여 표시하는 컴포넌트
const MenuItemTextWithCompany = ({ name, companyName }) => {
  if (!name) return null;
  
  // 회사명이 있는 경우
  if (companyName && companyName.trim()) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'normal', lineHeight: 1.2 }}>
          {name}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '0.65rem',
            color: 'text.secondary',
            fontStyle: 'italic',
            mt: 0.1,
            lineHeight: 1.1,
            opacity: 0.8
          }}
        >
          {companyName}
        </Typography>
      </Box>
    );
  }
  
  // 회사명이 없는 경우
  return (
    <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
      {name}
    </Typography>
  );
};

const TestReportTable = ({
  gproject,
  reports,
  onAddSubstitute,
  onComparisonDocumentUpdated,
  onViewDocument,
  onSubstituteDocumentSelected
}) => {
  const [showAll, setShowAll] = useState(true);
  const [showOnlyMissingDocs, setShowOnlyMissingDocs] = useState(false);
  const [showOnlyExpiredDocs, setShowOnlyExpiredDocs] = useState(false);
  const [documentViewerDrawerOpen, setDocumentViewerDrawerOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  
  // 팝업 메뉴 관련 state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isComparisonMenu, setIsComparisonMenu] = useState(false);

  // 메뉴 상태 보존을 위한 state 추가
  const [savedMenuState, setSavedMenuState] = useState({
    anchorEl: null,
    selectedRow: null,
    isComparisonMenu: false
  });

  // 대체성적서 다이얼로그 관련 state 추가
  const [substituteDialogOpen, setSubstituteDialogOpen] = useState(false);
  const [folderSubstituteDialogOpen, setFolderSubstituteDialogOpen] = useState(false);
  const [selectedRowForSubstitute, setSelectedRowForSubstitute] = useState(null);

  // 선택된 대체성적서 상태 추가
  const [selectedSubstituteDocuments, setSelectedSubstituteDocuments] = useState({});
  
  // 시험성과대비표 다이얼로그 관련 state 추가
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);
  const [selectedRowForComparison, setSelectedRowForComparison] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);
  
  // 대체시험성과대비표 목록 상태 추가
  const [gprojectGTestComparisons, setGprojectGTestComparisons] = useState([]);
  
  const sessionUser = useSelector(state => state.session.sessionUser);
  const dispatch = useDispatch();
  
  const modifySelectedSubTestDirect = (gprojectId, selectedSubTest, deleteMode = false) => gprojectActions.modifySelectedSubTestDirect(gprojectId, selectedSubTest, deleteMode);
  
  // 프로젝트의 모든 대체시험성과대비표 조회 함수
  const loadGProjectGTestComparisons = async (gprojectId) => {
    try {
      const comparisons = await gprojectActions.selectGProjectGTestComparisonsDirect(gprojectId);
      console.log('대체시험성과대비표 목록 조회:', comparisons);
      setGprojectGTestComparisons(comparisons);
      return comparisons;
    } catch (error) {
      console.error('대체시험성과대비표 목록 조회 실패:', error);
      return [];
    }
  };

  // 특정 시험의 대체시험성과대비표 존재 여부 확인 함수
  const checkModifiedComparisonExists = (g04docuGTestId) => {
    return gprojectGTestComparisons.some(comparison => comparison.g04docuGTestId === g04docuGTestId);
  };

  // 원본 시험성과대비표 경로를 가져오는 API 호출 함수
  const getOriginalComparisonDocumentPath = async (row) => {
    
    let gclientId;
    if (row.division === 'PROCESS' && row.otherDocuments.length > 0) {
      const substituteDocument = row.otherDocuments.find(doc => doc.g04docuGTestId === row.g04docuGTestId);
      if (substituteDocument) {
        gclientId = substituteDocument.gclient.id;
      }
    } else {
      gclientId = row.gclientId;
    }

    const g04docuGTestId = row.g04docuGTestId;
    
    // 필수 속성 검증
    if (!gclientId || !g04docuGTestId) {
      console.error('필수 속성이 누락되었습니다:', { gclientId, g04docuGTestId });
      return null;
    }
    
    try {
      const gclientG04docuGTestComparisonMap = await g04docuGTestComparisonActions.selectByGClientIdAndG04docuGTestIdDirect(gclientId ? gclientId : row.gclientId, g04docuGTestId);
      if (gclientG04docuGTestComparisonMap?.documentPath) {
        return gclientG04docuGTestComparisonMap.documentPath;
      }
      console.error('원본 시험성과대비표 경로 조회 실패');
      return null;
    } catch (error) {
      console.error('원본 시험성과대비표 경로 조회 실패:', error);
      return null;
    }
  };

  // 원본 시험성과대비표 존재 여부 확인 함수 추가
  const hasOriginalComparisonDocument = async (row) => {
    const originalPath = await getOriginalComparisonDocumentPath(row);
    return !!originalPath;
  };

  useEffect(() => {
    console.log('selectedSubstituteDocuments:', selectedSubstituteDocuments);
  }, [selectedSubstituteDocuments]);

  // 프로젝트가 변경될 때 대체시험성과대비표 목록 로드
  useEffect(() => {
    if (gproject?.id) {
      loadGProjectGTestComparisons(gproject.id);
    }
  }, [gproject?.id]);

  // 컴포넌트 마운트 시 기존에 저장된 대체성적서 정보를 상태에 로드
  useEffect(() => {
    if (reports && reports.length > 0) {
      const initialSelectedDocuments = {};
      
      reports.forEach(report => {
        // DB에 저장된 대체성적서 정보가 있으면 상태에 로드
        if (report.hasSubstituteReport && report.substituteDocumentPath && report.substituteDocumentName) {
          initialSelectedDocuments[report.id] = {
            documentPath: report.substituteDocumentPath,
            documentName: report.substituteDocumentName,
            documentType: report.substituteDocumentType || '대체성적서',
            gclientId: report.gclientId,
            gclient: report.gclient,
            isValid: report.isValid,
            isValidYN: report.isValidYN,
            gtestComparisonDocumentPath: report.comparisonDocumentPath,
            substituteFolderId: report.substituteFolderId || null,
          };
        }
      });
      
      if (Object.keys(initialSelectedDocuments).length > 0) {
        console.log('기존 대체성적서 정보 로드:', initialSelectedDocuments);
        setSelectedSubstituteDocuments(initialSelectedDocuments);
      }
    }
  }, [reports]);

  // 토글 버튼 핸들러 함수들
  const handleAllToggle = () => {
    setShowAll(true);
    setShowOnlyMissingDocs(false);
    setShowOnlyExpiredDocs(false);
  };
  
  const handleMissingDocsToggle = () => {
    setShowAll(false);
    setShowOnlyMissingDocs(prev => !prev);
    setShowOnlyExpiredDocs(false);
  };
  
  const handleExpiredDocsToggle = () => {
    setShowAll(false);
    setShowOnlyMissingDocs(false);
    setShowOnlyExpiredDocs(prev => !prev);
  };

  // 문서보기 함수
  const handleViewDocument = (documentPath, gclientId) => {
    const ownFile = sessionUser.type === 'ADMIN' || sessionUser.id === gclientId;
    const path = toFilePreviewUrl(documentPath, ownFile);
    // window.open(`${path}`, "미리보기", 'target="_self"');
    setCurrentUrl(path);
    // Drawer가 닫혀있을 때만 열기
    if (!documentViewerDrawerOpen) {
      setDocumentViewerDrawerOpen(true);
    }
  };

  // 마우스 호버 시 문서 미리보기 함수를 클릭 함수로 변경
  const handleDocumentClick = (documentPath, gclientId, row, options) => {
    if (!documentPath) return '';
    
    const ownFile = sessionUser.type === 'ADMIN' || sessionUser.id === gclientId;
    const path = toFilePreviewUrl(documentPath, ownFile);
    if (options?.previewOnly) return path;
    
    setCurrentUrl(path);
    setDocumentViewerDrawerOpen(true);
    return path;
  };

  const closeDocumentViewer = () => {
    setDocumentViewerDrawerOpen(false);
    
    // 이전 메뉴 상태가 있다면 복원
    if (savedMenuState.anchorEl && savedMenuState.selectedRow) {
      setTimeout(() => {
        setAnchorEl(savedMenuState.anchorEl);
        setSelectedRow(savedMenuState.selectedRow);
        setIsComparisonMenu(savedMenuState.isComparisonMenu);
        
        // 복원 후 저장된 상태 초기화
        setSavedMenuState({
          anchorEl: null,
          selectedRow: null,
          isComparisonMenu: false
        });
      }, 100); // 약간의 지연을 두어 문서 뷰어가 완전히 닫힌 후 메뉴가 나타나도록 함
    }
  };

  // 팝업 메뉴 핸들러
  const handleMenuClick = (event, row) => {
    console.log('handleMenuClick - row:', row);
    console.log('handleMenuClick - division:', row.division);
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setIsComparisonMenu(false);
  };

  const handleComparisonMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setIsComparisonMenu(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
    setIsComparisonMenu(false);
  };

  // 대체성적서 다이얼로그 열기
  const handleOpenSubstituteDialog = (row) => {
    setSelectedRowForSubstitute(row);
    if (row?.folderSource) {
      setFolderSubstituteDialogOpen(true);
    } else {
      setSubstituteDialogOpen(true);
    }
  };

  // 대체성적서 다이얼로그 닫기
  const handleCloseSubstituteDialog = () => {
    setSubstituteDialogOpen(false);
    setFolderSubstituteDialogOpen(false);
    setSelectedRowForSubstitute(null);
  };

  // 시험성과대비표 다이얼로그 열기
  const handleOpenComparisonDialog = (row) => {
    console.log("row:", row);
    setSelectedRowForComparison(row);
    setComparisonDialogOpen(true);
    setFullScreen(true); // 시험성과대비표 다이얼로그는 최대화로 열기
  };

  // 시험성과대비표 다이얼로그 닫기
  const handleCloseComparisonDialog = () => {
    setComparisonDialogOpen(false);
    setSelectedRowForComparison(null);
    setFullScreen(false);
  };

  // 원본 시험성과대비표 보기 함수
  const handleViewOriginalComparison = async (row) => {
    console.log('원본 시험성과대비표 보기:', row);
    
    try {
      // 원본 시험성과대비표 경로 가져오기
      const originalPath = await getOriginalComparisonDocumentPath(row);
    
      if (originalPath) {
        // 원본 시험성과대비표가 있으면 문서 뷰어로 열기
        handleDocumentClick(originalPath, row.gclientId, row);
      } else {
        alert('원본 시험성과대비표를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('원본 시험성과대비표 조회 실패:', error);
      alert('원본 시험성과대비표 조회에 실패했습니다.');
    }
  };

  // 시험성과대비표 삭제 함수
  const handleDeleteComparisonReport = async (selectedRow) => {
    console.log('시험성과대비표 삭제:', selectedRow);
    
    let gclientId;              
    if (selectedRow.division === 'PROCESS' && selectedRow.otherDocuments.length > 0) {
      const substituteDocument = selectedRow.otherDocuments.find(doc => doc.g04docuGTestId === selectedRow.g04docuGTestId);
      if (substituteDocument) {
        gclientId = substituteDocument.gclient.id;
      }
    }
    
    // DB에서 시험성과대비표 삭제
    const gprojectId = gproject?.id;
    const { g04docuGTestId } = selectedRow;
    
    if (gprojectId && g04docuGTestId) {
      console.log('DB 삭제할 데이터:', { gprojectId: gprojectId, g04docuGTestId: g04docuGTestId });
      
      if (onComparisonDocumentUpdated) {
        // 대체시험성과대비표 문서를 삭제하므로 원본 시험성과대비표 문서경로 (없는 경우 빈 문자열) 전달
        try {
          const gclientG04docuGTestComparisonMap = await g04docuGTestComparisonActions.selectByGClientIdAndG04docuGTestIdDirect(gclientId ? gclientId : selectedRow.gclientId, g04docuGTestId);
          const orgComparisonDocumentPath = gclientG04docuGTestComparisonMap?.documentPath || "";
          onComparisonDocumentUpdated(selectedRow.id, orgComparisonDocumentPath);
        } catch (error) {
          console.error('대체시험성과대비표 문서 삭제 실패:', error);
          if (error.serverResponse.code === 12047) {
            onComparisonDocumentUpdated(selectedRow.id, "");
          } else {
            alert('대체시험성과대비표 문서 삭제 실패: 원본 시험성과대비표 문서 조회 실패');
          }
        }
      }

      try {
        await gprojectActions.removeGProjectGTestComparisonDirect(gprojectId, g04docuGTestId);
        // 성공 메시지 표시
        alert('시험성과대비표가 삭제되었습니다.');
        // 대체시험성과대비표 목록 새로고침
        await loadGProjectGTestComparisons(gprojectId);
      } catch (error) {
        console.error('시험성과대비표 삭제 실패:', error);
        alert('시험성과대비표 삭제에 실패했습니다.');
      }
    }
    
    handleMenuClose();
  };

  // 대체성적서 선택 시 호출되는 콜백 함수
  const handleSubstituteDocumentSelected = async (selectedRow, selectedDocument) => {    
    console.log({selectedRow, selectedDocument});
   
    if (!selectedDocument) return;
    
    // selectedRowForSubstitute가 올바른 G04docuGTestManagement의 row 정보
    const originalRow = selectedRowForSubstitute;
    if (!originalRow) {
      console.error('originalRow가 없습니다.');
      return;
    }
    
    // 선택된 문서 정보 구성
    const selectedDocumentInfo = {
      g04docuGTestId: selectedDocument.g04docuGTestId,
      documentPath: selectedDocument.documentPath,
      name: selectedDocument.name,
      documentType: selectedDocument.documentType || '대체성적서',
      substitute: 'Y',
      substituteDocumentPath: selectedDocument.documentPath,
      // G04docuGTestManagement에서 선택한 문서의 경우 gclientId와 gclient 정보 추가 : TODO : 자재승인서 생성시 원본대조필 대체성적서 회사의 것이 출력되면 안되므로 확인해볼 것
      // gclientId: selectedDocument.gclientId,
      // gclient: selectedDocument.gclient,
      isValid: selectedDocument.valid,
      gtestComparisonDocumentPath: selectedDocument.gtestComparisonDocumentPath || originalRow.comparisonDocumentPath,
      substituteFolderId: selectedDocument.substituteFolderId || null,
    };
    
    // 선택된 대체성적서 상태 업데이트
    setSelectedSubstituteDocuments(prev => {
      const newState = {
        ...prev,
        [originalRow.id]: {
          g04docuGTestId: selectedDocument.g04docuGTestId,
          documentPath: selectedDocument.documentPath,
          documentName: selectedDocument.name,
          documentType: selectedDocument.documentType || '대체성적서',
          gclientId: selectedDocument.gclientId,
          gclient: selectedDocument.gclient,
          isValid: selectedDocument.isValid,
          isValidYN: selectedDocument.isValidYN,
          gtestComparisonDocumentPath: selectedDocument.gtestComparisonDocumentPath || originalRow.comparisonDocumentPath,
          substituteFolderId: selectedDocument.substituteFolderId || null,
        }
      };
      console.log('selectedSubstituteDocuments 업데이트:', {
        originalRowId: originalRow.id,
        selectedDocument,
        newState,
        g04docuGTestId: selectedDocument.g04docuGTestId
      });
      return newState;
    });
    
    console.log('대체문서 선택 (SubstituteDocumentDialog):', originalRow, selectedDocument);
    console.log('전달할 selectedDocumentInfo:', selectedDocumentInfo);
    
    // 부모 컴포넌트에 선택된 문서 정보 전달
    if (onSubstituteDocumentSelected) {
      onSubstituteDocumentSelected(originalRow, selectedDocumentInfo);
    }
    
    // DB에 저장
    console.log('DB 저장 시작 (SubstituteDocumentDialog):', selectedDocumentInfo);
    
    const gprojectId = gproject?.id;
    if (gprojectId && selectedDocumentInfo) {
      const selectedSubTest = {
        id: originalRow.id,
        g04docuGTestId: selectedDocumentInfo.g04docuGTestId,
        substituteDocumentPath: selectedDocumentInfo.documentPath,
        substituteDocumentName: selectedDocumentInfo.name,
        substituteDocumentType: selectedDocumentInfo.documentType,
        substituteDocumentValid: selectedDocumentInfo.isValid,
        substituteDocumentValidYN: selectedDocumentInfo.isValidYN,
        gtestComparisonDocumentPath: selectedDocumentInfo.gtestComparisonDocumentPath,
        substituteFolderId: selectedDocumentInfo.substituteFolderId || null,
        gclientId: originalRow.gclientId,
      };
      
      console.log('DB 저장할 데이터 (SubstituteDocumentDialog):', { gprojectId: gprojectId, site: gproject.site, selectedSubTest });
      
      try {
        await modifySelectedSubTestDirect(gprojectId, selectedSubTest);
        // DB 저장 성공 후 다이얼로그 닫기
        handleCloseSubstituteDialog();
        // 성공 메시지 표시
        alert('대체성적서가 첨부되었습니다.');
      } catch (error) {
        console.error('대체성적서 저장 실패:', error);
      }
    }
  };

  // 대체성적서 제거 함수
  const handleRemoveSubstituteDocument = async (selectedRow) => {
    console.log('대체성적서 제거:', selectedRow);

    // 1. 대체시험성과대비표가 있는지 확인
    const gprojectId = gproject?.id;
    const g04docuGTestId = selectedRow.g04docuGTestId;

    if (gprojectId && g04docuGTestId) {
      try {
        const comparison = gprojectGTestComparisons.find(
          c => c.g04docuGTestId === g04docuGTestId
        );
        if (comparison) {
          alert('이 대체성적서와 연결된 대체시험성과대비표가 존재합니다.\n먼저 대체시험성과대비표를 삭제해 주세요.');
          return;
        }
      } catch (error) {
        // 조회 실패 시는 그냥 삭제 진행(혹은 에러 처리)
        console.error('대체시험성과대비표 조회 실패:', error);
      }
    }
    
    // 메뉴를 즉시 닫기 (팝업메뉴 중복/갱신 방지)
    handleMenuClose();
    
    // 선택된 대체성적서 상태에서 제거
    setSelectedSubstituteDocuments(prev => {
      const newState = { ...prev };
      delete newState[selectedRow.id];
      return newState;
    });
    
    // 부모 컴포넌트에 제거 정보 전달
    if (onSubstituteDocumentSelected) {
      onSubstituteDocumentSelected(selectedRow, null);
    }
    
    // DB에서 제거 (deleteMode = true로)
    // const gprojectId = gproject?.id;
    if (gprojectId) {
      const selectedSubTest = {
        id: selectedRow.id
      };
      
      console.log('DB 삭제할 데이터:', { gprojectId: gprojectId, site: gproject.site, selectedSubTest });
      
      try {
        await modifySelectedSubTestDirect(gprojectId, selectedSubTest, true);
        // 성공 메시지 표시
        alert('대체성적서가 제거되었습니다.');
      } catch (error) {
        console.error('대체성적서 제거 실패:', error);
      }
    }
  };

  // 팝업 메뉴에서 아이콘 클릭 시 처리 (메뉴 상태 보존)
  const handleMenuIconClick = (documentPath, gclientId, row) => {
    if (!documentPath) return;
    
    const ownFile = sessionUser.type === 'ADMIN' || sessionUser.id === gclientId;
    const path = toFilePreviewUrl(documentPath, ownFile);
    
    // 현재 메뉴 상태를 보존
    setSavedMenuState({
      anchorEl: anchorEl,
      selectedRow: selectedRow,
      isComparisonMenu: isComparisonMenu
    });
    
    setCurrentUrl(path);
    setDocumentViewerDrawerOpen(true);
    // 메뉴 닫기
    handleMenuClose();
  };

  // 대체시험성과대비표 아이콘 클릭 시 처리
  const handleComparisonMenuIconClick = async (gclientId, row) => {
    // 대체시험성과대비표에서 documentPath 찾기
    const selectedGTestComparisons = gprojectGTestComparisons.filter(g => g.g04docuGTestId === row.g04docuGTestId);
    const documentPath = selectedGTestComparisons.length === 1 ? selectedGTestComparisons[0].documentPath : null;
    
    if (!documentPath) return;
    
    // 기존 handleMenuIconClick과 동일한 로직 사용
    handleMenuIconClick(documentPath, gclientId, row);
  };

  // 팝업 메뉴에서 문서 선택 시 처리 (대체문서 선택 기능)
  const handleMenuDocumentSelect = async (selectedRow, doc, documentType) => {
    // 가공이고, 대체시험성과대비표가 있으면 경고 후 return
    if (isProcessingType(selectedRow.division)) {
      const gprojectId = gproject?.id;
      const g04docuGTestId = selectedRow.g04docuGTestId;
      if (gprojectId && g04docuGTestId) {
        console.log('gprojectGTestComparisons:', gprojectGTestComparisons);
        const comparison = gprojectGTestComparisons.find(
          c => c.g04docuGTestId === g04docuGTestId
        );
        if (comparison) {
          alert('이 대체성적서와 연결된 대체시험성과대비표가 존재합니다.\n먼저 대체시험성과대비표를 삭제해 주세요.');
          handleMenuClose();
          return;
        }
      }
    }

    const {
      hasExactTestReport, // true일 경우 딱 맞는 성적서, 따라서 여기서는 항상 false임
      hasPerformanceReport,
      hasSubstituteReport,
      documentPath, // 대체성적서, 관련 KCC 성적서 경로도 여기에 기록됨
      performanceDocumentPath, // 성능확인서
      substituteDocumentPath, // 대체성적서
      gclientId,
      gclient,
    } = selectedRow;

    console.log('대체성적서 선택:', { selectedRow, doc, documentType });

    // 성능확인서인 경우 substitute를 "N"으로 설정
    if (documentType === 'performance') {
      const selectedDocumentInfo = {
        documentPath: doc?.path,
        name: selectedRow.productName,
        documentType: 'performance',
        substitute: 'N',
        gtestComparisonDocumentPath: "",
      };
      
      // 선택된 대체성적서 상태 업데이트
      setSelectedSubstituteDocuments(prev => ({
        ...prev,
        [selectedRow.id]: {
          documentPath: doc?.path,
          documentName: doc?.name,
          documentType: 'performance',
          gtestComparisonDocumentPath: "",
          substituteFolderId: null,
        }
      }));
      
      console.log('전달할 selectedDocumentInfo:', selectedDocumentInfo);
      
      // 부모 컴포넌트에 선택된 문서 정보 전달
      if (onSubstituteDocumentSelected) {
        onSubstituteDocumentSelected(selectedRow, selectedDocumentInfo);
      }

      // 메뉴를 먼저 닫기
      handleMenuClose();

          // DB에 저장 (성능확인서도 저장)
      console.log('DB 저장 시작 (성능확인서):', selectedDocumentInfo);
      
      const gprojectId = gproject?.id;
      if (gprojectId && selectedDocumentInfo) {
        const selectedSubTest = {
          id: selectedRow.id,
          substituteDocumentPath: selectedDocumentInfo.documentPath,
          substituteDocumentName: selectedDocumentInfo.documentName,
          substituteDocumentType: selectedDocumentInfo.documentType,
          substituteDocumentValid: null, // 성능확인서는 유효성 검사 없음
          substituteDocumentValidYN: null,
          gtestComparisonDocumentPath: ""
        };
        
        console.log('DB 저장할 데이터 (성능확인서):', { gprojectId: gprojectId, site: gproject.site, selectedSubTest });
        
        try {
          await modifySelectedSubTestDirect(gprojectId, selectedSubTest);
          // 성공 메시지 표시 (메뉴 닫힌 후)
          // setTimeout(() => {
            alert('성능확인서가 첨부되었습니다.');
          // }, 1000);
        } catch (error) {
          console.error('성능확인서 저장 실패:', error);
        }
      }
      
      return;
    }
    
    // 다른 성적서인 경우 substitute를 "Y"로 설정
    const selectedDocumentInfo = {
      g04docuGTestId: doc?.g04docuGTestId,
      documentPath: doc?.path,
      name: doc?.name,
      documentName: doc?.name, // DB 저장용 필드 추가
      documentType: documentType,
      substitute: 'Y',
      substituteDocumentPath: doc?.path || selectedRow.documentPath,
      // otherDocuments에서 선택한 문서의 경우 gclientId와 gclient 정보 추가 : TODO : 자재승인서 생성시 원본대조필 대체성적서 회사의 것이 출력되면 안되므로 확인해볼 것
      // gclientId: doc?.gclientId,
      gclient: doc?.gclient,
      isValid: doc?.isValid,
      isValidYN: doc?.isValidYN,
      // 선택한 문서에 딸려있는 시험성과대비표 경로도 함께 저장
      gtestComparisonDocumentPath: doc?.gtestComparisonDocumentPath || selectedRow.comparisonDocumentPath,
    };
    
    // 선택된 대체성적서 상태 업데이트
    setSelectedSubstituteDocuments(prev => ({
      ...prev,
      [selectedRow.id]: {
        g04docuGTestId: doc?.g04docuGTestId,
        documentPath: doc?.path,
        documentName: doc?.name,
        documentType: documentType,
        gclientId: doc?.gclientId,
        gclient: doc?.gclient,
        isValid: doc?.isValid,
        isValidYN: doc?.isValidYN,
        gtestComparisonDocumentPath: doc?.gtestComparisonDocumentPath || selectedRow.comparisonDocumentPath,
        substituteFolderId: null,
        // substitute: 'Y',
        // substituteDocumentPath: doc?.path || selectedRow.documentPath
      }
    }));
    
    console.log('대체문서 선택:', selectedRow, doc, documentType);
    console.log('전달할 selectedDocumentInfo:', selectedDocumentInfo);
    
    // 부모 컴포넌트에 선택된 문서 정보 전달
    if (onSubstituteDocumentSelected) {
      onSubstituteDocumentSelected(selectedRow, selectedDocumentInfo);
    }
    
    // 메뉴를 먼저 닫기
    handleMenuClose();
    
    // DB에 저장 (스텝 1: 저장만 구현)
    console.log('DB 저장 시작:', selectedDocumentInfo);
    
    const gprojectId = gproject?.id;
    if (gprojectId && selectedDocumentInfo) {
      const selectedSubTest = {
        id: selectedRow.id,
        g04docuGTestId: selectedDocumentInfo.g04docuGTestId,
        substituteDocumentPath: selectedDocumentInfo.documentPath,
        substituteDocumentName: selectedDocumentInfo.documentName,
        substituteDocumentType: selectedDocumentInfo.documentType,
        substituteDocumentValid: selectedDocumentInfo.isValid,
        substituteDocumentValidYN: selectedDocumentInfo.isValidYN,
        // 선택한 문서에 딸려있는 시험성과대비표 경로도 함께 저장
        gtestComparisonDocumentPath: selectedDocumentInfo.gtestComparisonDocumentPath
      };
      
      console.log('DB 저장할 데이터:', { gprojectId: gprojectId, site: gproject.site, selectedSubTest });
      try {
        await modifySelectedSubTestDirect(gprojectId, selectedSubTest);
        // 성공 메시지 표시
        // setTimeout(() => {
          alert('대체성적서가 첨부되었습니다.');
        // }, 1000);
      } catch (error) {
        console.error('대체성적서 저장 실패:', error);
      }
    }
  };

  // 선택된 대체성적서 확인 함수
  const getSelectedSubstituteDocument = (rowId) => {
    return selectedSubstituteDocuments[rowId];
  };

  // 현재 선택된 문서 확인 함수
  const getCurrentSelectedDocument = (row) => {
    // 1. 사용자가 선택한 대체성적서가 있으면 그것을 우선 (임시 상태)
    const selectedSubstitute = getSelectedSubstituteDocument(row.id);
    if (selectedSubstitute) {
      console.log('getCurrentSelectedDocument - selectedSubstitute:', selectedSubstitute);
      
      // otherDocuments에서 선택한 문서의 정보 찾기
      let isValid = null;
      let documentName = selectedSubstitute.documentName;
      let documentType = selectedSubstitute.documentType;
      
      if (row.otherDocuments && Array.isArray(row.otherDocuments) && row.otherDocuments.length > 0) {
        const selectedDoc = row.otherDocuments.find(doc => 
          doc.path === selectedSubstitute.documentPath && 
          doc.gclientId === selectedSubstitute.gclientId
        );
        if (selectedDoc) {
          isValid = selectedDoc.isValid;
          // 후보군에서 선택한 경우 원래 문서 이름과 타입 사용
          documentName = selectedDoc.name;
          documentType = selectedDoc.type || '대체성적서';
        } else {
          // otherDocuments에서 못 찾은 경우 selectedSubstitute의 정보 사용
          isValid = selectedSubstitute.isValid;
          documentName = selectedSubstitute.documentName;
          documentType = selectedSubstitute.documentType;
        }
      } else {
        isValid = selectedSubstitute.isValid;
        documentName = selectedSubstitute.documentName;
        documentType = selectedSubstitute.documentType;
      }
      
      const result = {
        documentPath: selectedSubstitute.documentPath,
        documentName: documentName,
        documentType: documentType,
        gclientId: selectedSubstitute.gclientId,
        gclient: selectedSubstitute.gclient,
        isValid: isValid,
        isValidYN: selectedSubstitute.isValidYN,
        gtestComparisonDocumentPath: selectedSubstitute.gtestComparisonDocumentPath,
      };
      
      console.log('getCurrentSelectedDocument - selectedSubstitute 결과:', {
        selectedSubstitute,
        result,
        otherDocuments: row.otherDocuments
      });
      
      return result;
    }

    // 2. 가공인 경우
    if (isProcessingType(row.division)) {
      // 대체성적서가 있으면 대체성적서가 선택됨 (DB에 저장된 내용)
      if (row.hasSubstituteReport && row.documentPath) {
        const result = {
          documentPath: row.documentPath,
          documentName: row.substituteDocumentName || '대체성적서',
          documentType: row.substituteDocumentType || '대체성적서',
          substitute: 'Y',
          gtestComparisonDocumentPath: row.comparisonDocumentPath,
          isValid: row.isValid,
          isValidYN: row.isValidYN,
          // 대체성적서의 유효성 정보는 valid와 validYN 필드에 저장되어 있음
        };
        console.log('가공 대체성적서 선택:', result);
        return result;
      }
      
      // 성능확인서가 있고 딱 맞는 성적서가 없으면 성능확인서가 선택됨
      if (row.hasPerformanceReport && row.performanceDocumentPath && !row.hasExactTestReport) {
        return {
          documentPath: row.performanceDocumentPath,
          documentName: '성능확인서',
          documentType: 'performance',
          substitute: 'N',
          gtestComparisonDocumentPath: row.comparisonDocumentPath
        };
      }
    } else {
      // 가공 외인 경우
      // 대체성적서가 있으면 대체성적서가 선택됨
      if (row.hasSubstituteReport && row.documentPath) {
        const result = {
          documentPath: row.documentPath,
          documentName: row.substituteDocumentName || '대체성적서',
          documentType: row.substituteDocumentType || '대체성적서',
          substitute: 'Y',
          gtestComparisonDocumentPath: row.comparisonDocumentPath,
          isValid: row.isValid,
          isValidYN: row.isValidYN,
          // 대체성적서의 유효성 정보는 valid와 validYN 필드에 저장되어 있음
        };
        console.log('가공 외 대체성적서 선택:', result);
        return result;
      }
    }

    // 아무것도 선택되지 않은 경우
    return null;
  };

  // 가공 여부 확인 함수
  const isProcessingType = (division) => {
    console.log('division:', division);
    return division === 'PROCESS';
  };

  // 성적서 데이터를 테이블 형태로 변환
  const tableData = useMemo(() => {
    if (!reports || reports.length === 0) {
      return {
        headers: [],
        rows: []
      };
    }

    const headers = [
      {
        type: 'HEADER',
        label: '구분',
        width: '8%',
      },
      {
        type: 'HEADER', 
        label: '업체명',
        width: '15%',
      },
      {
        type: 'HEADER',
        label: '제품명',
        // width: '25%',
      },
      {
        type: 'HEADER',
        label: '성적서',
        width: '10%',
      },
      {
        type: 'HEADER',
        label: '대체성적서',
        width: '12%',
      },
      {
        type: 'HEADER',
        label: '시험성과대비표',
        width: '10%',
      },
      {
        type: 'HEADER',
        label: '대체시험성과대비표',
        width: '10%',
      },
      // {
      //   type: 'HEADER',
      //   label: '작업',
      //   rowspan: 2,
      //   width: '14%',
      // },
    ];

    // 필터링 로직
    const filteredRows = reports.filter(report => {
      // 전체 보기일 때는 모든 항목 표시
      if (showAll) {
        return true;
      }

      // 성적서 없음 필터 - 원본성적서(딱 맞는 성적서) 또는 대체성적서(현재 선택된 대체성적서) 둘 다 없을 때만 true
      if (showOnlyMissingDocs) {
        // 가공일 때는 성능확인서도 성적서가 있는 것으로 간주
        const hasAnyDocument = report.hasExactTestReport || 
                              selectedSubstituteDocuments[report.id] ||
                              (isProcessingType(report.division) && report.hasPerformanceReport);
        return !hasAnyDocument;
      }

      // 만료일 경과 필터 - 딱 맞는 성적서가 있지만 만료된 경우
      if (showOnlyExpiredDocs) {
        return report.hasExactTestReport && report.isValid === false;
      }

      return true;
    });

    // 중복 제거 (같은 ID를 가진 항목 중 첫 번째만 유지)
    const uniqueRows = filteredRows.filter((row, index, self) => 
      index === self.findIndex(r => r.id === row.id)
    );

    console.log('필터링 결과:', {
      originalCount: reports.length,
      filteredCount: filteredRows.length,
      uniqueCount: uniqueRows.length,
      showAll,
      showOnlyMissingDocs,
      showOnlyExpiredDocs
    });

    const rows = uniqueRows.map((report, index) => {
      // 대체성적서가 선택된 경우 해당 g04docuGTestId 사용
      const selectedSubstitute = selectedSubstituteDocuments[report.id];
      const g04docuGTestId = selectedSubstitute?.g04docuGTestId || report.g04docuGTestId;
      
      return {
        id: report.id,
        g04docuGTestId: g04docuGTestId, // 대체성적서의 g04docuGTestId 우선 사용
        division: report.division,
        company: report.company,
        gclient: report.gclient,
        gclientId: report.gclientId,
        productName: report.productName,
        hasExactTestReport: report.hasExactTestReport,
        hasTestReport: report.hasTestReport,
        hasPerformanceReport: report.hasPerformanceReport,
        hasSubstituteReport: report.hasSubstituteReport,
        hasComparisonReport: report.hasComparisonReport,
        status: report.status,
        isValid: report.isValid,
        isValidYN: report.isValidYN,
        documentPath: report.documentPath,
        currentDocumentPath: selectedSubstitute?.documentPath || report.documentPath,
        analysisGclientId: selectedSubstitute?.gclientId || selectedSubstitute?.gclient?.id || report.gclient?.id || report.gclientId,
        performanceDocumentPath: report.performanceDocumentPath,
        substituteDocumentPath: report.substituteDocumentPath,
        substituteDocumentName: report.substituteDocumentName,
        substituteDocumentType: report.substituteDocumentType,
        otherDocuments: report.otherDocuments,
        comparisonDocumentPath: report.comparisonDocumentPath,
        g04docuGCertificationId: report.g04docuGCertificationId,
        g04docuGCertificationName: report.g04docuGCertificationName,
        gcomponentItemName: report.gcomponentItemName, // 원판인 경우 필요한 정보 추가
        folderSource: report.folderSource,
        productFolderId: report.productFolderId,
        code: report.code,
        substituteFolderId: selectedSubstitute
          ? (selectedSubstitute.substituteFolderId || null)
          : (report.substituteFolderId || null),
        gstandardNo: report.gstandardNo,
        opened: report.opened,
        isModifiedComparisonOnly: report.isModifiedComparisonOnly, // isModifiedComparisonOnly 추가
        classification: report.classification || "",
        classificationFt: report.classificationFt || "",
        gstandardId: report.gstandardId || null,
      };
    });
    console.log('rows:', rows);
    return { headers, rows };
  }, [reports, showAll, showOnlyMissingDocs, showOnlyExpiredDocs, selectedSubstituteDocuments]);

  const getBackgroundColor = (type) => {
    switch (type) {
      case 'HEADER':
        return '#1976d2';
      default:
        return 'transparent';
    }
  };

  const getDocumentIcon = (hasDocument, documentPath, onClick, row, isComparison = false) => {
    // opened가 false인 경우 LockIcon 아이콘 표시
    if (row.opened === false) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="문서가 닫혀있습니다.">
            <IconButton
              size="small"
              sx={{ 
                color: '#ccc',
                '&:hover': {
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <LockIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // 시험성과대비표인 경우
    if (isComparison) {
      if (hasDocument && documentPath) {
        let iconColor = '#2EA2DE'; // 기본 파란색
        
        if (row.isValid === false) {
          iconColor = '#DB4E2E'; // 만료된 경우 빨간색
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Tooltip title="시험성과대비표 보기">
              <IconButton
                size="small"
                onClick={() => handleDocumentClick(documentPath, row.gclientId, row)}
                sx={{ color: iconColor }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      } else {
        // 시험성과대비표가 없는 경우 Block 아이콘 표시 (클릭 가능)
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Tooltip title="여기를 눌러 시험성과대비표를 작성하세요.">
              <IconButton
                size="small"
                onClick={(e) => handleComparisonMenuClick(e, row)}
                sx={{ 
                  color: '#DBDBDB',
                  '&:hover': {
                    color: '#1976d2',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <VisibilityOffIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
    
    // 성적서인 경우
    // 딱 맞는 성적서가 있는 경우
    if (row.hasExactTestReport && row.documentPath) {
      let iconColor = '#2EA2DE'; // 기본 파란색
      
      if (row.isValid === false) {
        iconColor = '#DB4E2E'; // 만료된 경우 빨간색
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="서류보기" placement="left">
            <IconButton
              size="small"
              onClick={() => handleDocumentClick(documentPath, row.gclientId, row)}
              sx={{ color: iconColor }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    console.log({ row });
    const { hasPerformanceReport, hasSubstituteReport, otherDocuments, isValid, isValidYN } = row;
    // 딱 맞는 성적서가 없는 경우
    const hasRelatedDocuments = hasPerformanceReport || 
                               hasSubstituteReport || 
                               (otherDocuments && otherDocuments.length > 0);
    
    // 현재 선택된 문서 확인 (화면에서 선택한 것 우선, 없으면 기존 선택된 것)
    const currentSelected = getCurrentSelectedDocument(row);
    
    let icon = <VisibilityOffIcon />;
    let iconColor = '#2EA2DE'; // 기본 색상
    let documentLabel = '';
    
    // 현재 선택된 문서가 있으면 해당 문서를 보여주는 아이콘으로 변경
    if (currentSelected) {
      icon = <VisibilityIcon />;
      
      // 문서 타입에 따른 라벨 설정
      if (currentSelected.documentType === 'performance') {
        documentLabel = '성능확인서';
        iconColor = DOC_STATUS_COLOR.performance;
      } else {
        documentLabel = currentSelected.documentName || '대체성적서';
        if (currentSelected.isValid !== null && currentSelected.isValid !== undefined) {
          iconColor = substituteIconColor(currentSelected.isValid);
        } else if (currentSelected.gclientId && otherDocuments) {
          const selectedDoc = otherDocuments.find(doc =>
            doc.path === currentSelected.documentPath &&
            doc.gclientId === currentSelected.gclientId
          );
          iconColor = substituteIconColor(selectedDoc ? selectedDoc.isValid : isValid);
        } else {
          iconColor = substituteIconColor(isValid);
        }
      }
    } else {
      iconColor = DOC_STATUS_COLOR.missing;
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Tooltip title="여기를 눌러 대체성적서를 추가 또는 변경하실 수 있습니다.">
          <IconButton
            size="small"
            onClick={(e) => {
              if (hasRelatedDocuments) {
                handleMenuClick(e, row);
              } else {
                // 관련 문서가 없으면 바로 다이얼로그 표시
                handleOpenSubstituteDialog(row);
              }
            }}
            sx={{ color: iconColor }}
          >
            {icon}
          </IconButton>
        </Tooltip>
        {documentLabel && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.65rem',
              color: 'text.secondary',
              mt: 0.5,
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            {documentLabel}
          </Typography>
        )}
      </Box>
    );
  };

  // 원본성적서 아이콘을 표시하는 함수 추가
  const getOriginalDocumentIcon = (row) => {
    // opened가 false인 경우 LockIcon 아이콘 표시
    if (row.opened === false) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="문서가 닫혀있습니다.">
            <IconButton
              size="small"
              sx={{ 
                color: '#ccc',
                '&:hover': {
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <LockIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // 딱 맞는 성적서가 있는 경우
    if (row.hasExactTestReport && row.documentPath) {
      let iconColor = '#2EA2DE'; // 기본 파란색
      
      if (row.isValid === false) {
        iconColor = '#DB4E2E'; // 만료된 경우 빨간색
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="서류보기" placement="left">
            <IconButton
              size="small"
              onClick={() => handleDocumentClick(row.documentPath, row.gclientId, row)}
              sx={{ color: iconColor }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    // 딱 맞는 성적서가 없는 경우 (가공의 경우 성능확인서만 있어도 없는 것으로 처리)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Tooltip title="서류없음">
          <IconButton
            size="small"
            sx={{ 
              color: '#DBDBDB',
            }}
          >
            <VisibilityOffIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // 현재 선택된 성적서 아이콘을 표시하는 함수 추가
  const getCurrentDocumentIcon = (row) => {
    // opened가 false인 경우 LockIcon 아이콘 표시
    if (row.opened === false) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="문서가 닫혀있습니다.">
            <IconButton
              size="small"
              sx={{ 
                color: '#ccc',
                '&:hover': {
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <LockIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // 딱 맞는 성적서가 있는 경우 - 빈 상태 (대체 불필요)
    if (row.hasExactTestReport && row.documentPath) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="성적서가 있어 대체가 불필요합니다.">
            <Box sx={{ width: 24, height: 24 }} />
          </Tooltip>
        </Box>
      );
    }
    
    // 딱 맞는 성적서가 없는 경우 - 현재 선택된 문서 표시
    const { hasPerformanceReport, hasSubstituteReport, otherDocuments, isValid, isValidYN } = row;
    const hasRelatedDocuments = hasPerformanceReport || 
                               hasSubstituteReport || 
                               (otherDocuments && otherDocuments.length > 0);
    
    // 현재 선택된 문서 확인
    const currentSelected = getCurrentSelectedDocument(row);
    
    let icon = <VisibilityOffIcon />;
    let iconColor = '#2EA2DE'; // 기본 색상
    let documentLabel = '';
    
    // 현재 선택된 문서가 있으면 해당 문서를 보여주는 아이콘으로 변경
    if (currentSelected) {
      icon = <VisibilityIcon />;
      
      // 문서 타입에 따른 라벨 설정
      if (currentSelected.documentType === 'performance') {
        documentLabel = '성능확인서';
        iconColor = DOC_STATUS_COLOR.performance;
      } else {
        documentLabel = currentSelected.documentName || '대체성적서';
        if (currentSelected.isValid !== null && currentSelected.isValid !== undefined) {
          iconColor = substituteIconColor(currentSelected.isValid);
        } else if (currentSelected.gclientId && otherDocuments) {
          const selectedDoc = otherDocuments.find(doc =>
            doc.path === currentSelected.documentPath &&
            doc.gclientId === currentSelected.gclientId
          );
          iconColor = substituteIconColor(selectedDoc ? selectedDoc.isValid : isValid);
        } else {
          iconColor = substituteIconColor(isValid);
        }
      }
    } else {
      // 선택된 문서가 없는 경우 MoreHorizIcon 표시
      // icon = <MoreHorizIcon />;
      icon = <AddIcon />;
      // iconColor = '#DBDBDB';
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Tooltip title={currentSelected ? "여기를 눌러 대체성적서를 변경 또는 삭제하실 수 있습니다." : "여기를 눌러 대체성적서를 추가하실 수 있습니다."}>
          <IconButton
            size="small"
            onClick={(e) => {
              if (hasRelatedDocuments) {
                handleMenuClick(e, row);
              } else {
                // 관련 문서가 없으면 바로 다이얼로그 표시
                handleOpenSubstituteDialog(row);
              }
            }}
            sx={{ color: iconColor }}
          >
            {icon}
          </IconButton>
        </Tooltip>
        {documentLabel && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.65rem',
              color: 'text.secondary',
              mt: 0.5,
              textAlign: 'center',
              lineHeight: 1
            }}
          >
            {documentLabel}
          </Typography>
        )}
      </Box>
    );
  };

  // 시험성과대비표 아이콘을 표시하는 함수 추가
  const getOriginalComparisonIcon = (row) => {
    // opened가 false인 경우 LockIcon 아이콘 표시
    if (row.opened === false) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="문서가 닫혀있습니다.">
            <IconButton
              size="small"
              sx={{ 
                color: '#ccc',
                '&:hover': {
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <LockIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // 성적서나 대체성적서가 없는 경우 - 빈 상태 (시험성과대비표 의미 없음)
    const hasAnyTestReport = row.hasExactTestReport || selectedSubstituteDocuments[row.id];
    if (!hasAnyTestReport) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="성적서가 없어 시험성과대비표가 의미없습니다.">
            <Box sx={{ width: 24, height: 24 }} />
          </Tooltip>
        </Box>
      );
    }
    
    // 원본 시험성과대비표가 있는 경우 (대체시험성과대비표가 원본이 없는 경우는 제외)
    if (row.hasComparisonReport && row.comparisonDocumentPath && !row.isModifiedComparisonOnly) {
      let iconColor = '#2EA2DE'; // 기본 파란색
      
      if (row.isValid === false) {
        iconColor = '#DB4E2E'; // 만료된 경우 빨간색
      }

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="원본 서류보기" placement="left">
            <IconButton
              size="small"
              onClick={() => handleViewOriginalComparison(row)}
              sx={{ color: iconColor }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    // 대체시험성과대비표만 있는 경우 (원본이 없고 수정본만 있는 경우) - 서류없음으로 처리
    if (row.hasComparisonReport && row.comparisonDocumentPath && row.isModifiedComparisonOnly) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="서류없음">
            <IconButton
              size="small"
              sx={{ 
                color: '#DBDBDB',
              }}
            >
              <VisibilityOffIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    // 시험성과대비표가 없는 경우
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Tooltip title="서류없음">
          <IconButton
            size="small"
            sx={{ 
              color: '#DBDBDB',
            }}
          >
            <VisibilityOffIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // 현재 선택된 시험성과대비표 아이콘을 표시하는 함수 추가
  const getModifiedComparisonIcon = (row) => {
    // opened가 false인 경우 LockIcon 아이콘 표시
    if (row.opened === false) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="문서가 닫혀있습니다.">
            <IconButton
              size="small"
              sx={{ 
                color: '#DBDBDB',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <LockIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // 성적서나 대체성적서가 없는 경우 - 빈 상태 (시험성과대비표 의미 없음)
    const hasAnyTestReport = row.hasExactTestReport || selectedSubstituteDocuments[row.id];
    if (!hasAnyTestReport) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="성적서가 없어 시험성과대비표 작성이 불가능합니다.">
            <Box sx={{ width: 24, height: 24 }} />
          </Tooltip>
        </Box>
      );
    }

    // 대체시험성과대비표가 있는 경우 - 보기 아이콘으로 표시
    const modifiedExists = row.g04docuGTestId ? checkModifiedComparisonExists(row.g04docuGTestId) : false;
    if (modifiedExists) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="여기를 눌러 대체시험성과대비표를 보기 또는 수정하실 수 있습니다.">
            <IconButton
              size="small"
              onClick={(e) => handleComparisonMenuClick(e, row)}
              sx={{ 
                color: '#2EA2DE',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    // 원본 시험성과대비표가 있는 경우 - 수정 아이콘으로 표시 (바로 수정 다이얼로그 열기)
    if (row.hasComparisonReport && row.comparisonDocumentPath) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Tooltip title="여기를 눌러 대체시험성과대비표를 작성하실 수 있습니다.">
            <IconButton
              size="small"
              onClick={() => handleOpenComparisonDialog(row)}
              sx={{ 
                color: '#2EA2DE',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
    
    // 시험성과대비표가 없는 경우 - Add 아이콘으로 표시 (바로 작성 다이얼로그 열기)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Tooltip title="여기를 눌러 대체시험성과대비표를 새로 작성하실 수 있습니다.">
          <IconButton
            size="small"
            onClick={() => handleOpenComparisonDialog(row)}
            sx={{ 
              color: '#2EA2DE',
              '&:hover': {
                color: '#1976d2',
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // 성적서 첨부 완료 상태 계산
  const attachmentStatus = useMemo(() => {
    if (!reports || reports.length === 0) {
      return {
        total: 0,
        completed: 0,
        percentage: 0,
        status: 'none'
      };
    }

    let completed = 0;
    let total = reports.length;

    reports.forEach(report => {
      // 성적서가 있는지 확인 (원본 또는 대체)
      const hasTestReport = report.hasExactTestReport || 
                           selectedSubstituteDocuments[report.id] ||
                           (isProcessingType(report.division) && report.hasPerformanceReport);
      
      if (hasTestReport) {
        completed++;
      }
    });

    const percentage = Math.round((completed / total) * 100);
    
    let status = 'none';
    if (percentage === 100) {
      status = 'completed';
    } else if (percentage >= 80) {
      status = 'almost';
    } else if (percentage >= 50) {
      status = 'half';
    } else if (percentage > 0) {
      status = 'started';
    }

    return {
      total,
      completed,
      percentage,
      status
    };
  }, [reports, selectedSubstituteDocuments]);

  // 신호등 색상 결정
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50'; // 초록색
      case 'almost':
        return '#FF9800'; // 주황색
      case 'half':
        return '#FFC107'; // 노란색
      case 'started':
        return '#2196F3'; // 파란색
      default:
        return '#9E9E9E'; // 회색
    }
  };

  // 신호등 아이콘
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#4CAF50' }} />;
      case 'almost':
        return <WarningIcon sx={{ color: '#FF9800' }} />;
      case 'half':
        return <WarningIcon sx={{ color: '#FFC107' }} />;
      case 'started':
        return <SyncIcon sx={{ color: '#2196F3' }} />;
      default:
        return <CircleIcon sx={{ color: '#9E9E9E' }} />;
    }
  };

  // 상태 메시지
  const getStatusMessage = (status) => {
    switch (status) {
      case 'completed':
        return '모든 성적서가 첨부되었습니다!';
      case 'almost':
        return '거의 완료되었습니다. 몇 개만 더 첨부해주세요.';
      case 'half':
        return '절반 정도 완료되었습니다. 계속 진행해주세요.';
      case 'started':
        return '성적서 첨부를 시작했습니다. 계속 진행해주세요.';
      default:
        return '성적서 첨부를 시작해주세요.';
    }
  };

  if (!reports || reports.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          표시할 성적서 데이터가 없습니다.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 성적서 첨부 완료 상태 표시 */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getStatusIcon(attachmentStatus.status)}
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
            성적서 첨부 현황
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={attachmentStatus.percentage}
              sx={{ 
                height: 12, 
                borderRadius: 6,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getStatusColor(attachmentStatus.status),
                  borderRadius: 6
                }
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 60, textAlign: 'right' }}>
            {attachmentStatus.completed}/{attachmentStatus.total} ({attachmentStatus.percentage}%)
          </Typography>
        </Box>
        
        <Alert 
          severity={attachmentStatus.status === 'completed' ? 'success' : 
                   attachmentStatus.status === 'almost' ? 'warning' : 
                   attachmentStatus.status === 'half' ? 'info' : 'info'}
          sx={{ 
            '& .MuiAlert-message': { 
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }
          }}
        >
          {getStatusMessage(attachmentStatus.status)}
        </Alert>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, gap: 2, flexWrap: 'wrap' }}>
        <DocumentStatusLegend />
        <ToggleButtonGroup size="small" exclusive color="primary">
          <ToggleButton
            value="all"
            selected={showAll}
            onChange={handleAllToggle}
          >
            {"전체"}
          </ToggleButton>
          <ToggleButton
            value="missingDocs"
            selected={showOnlyMissingDocs}
            onChange={handleMissingDocsToggle}
          >
            {"성적서 없음"}
          </ToggleButton>
          <ToggleButton
            value="expiredDocs"
            selected={showOnlyExpiredDocs}
            onChange={handleExpiredDocsToggle}
          >
            {"만료일 경과"}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* 테이블 */}
      <TableContainer
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          boxShadow: 'none',
          borderRadius: 0,
        }}
      >
        <Table
          size="small"
          sx={{
            borderCollapse: 'collapse',
            border: '1px solid rgba(0, 0, 0, 0.12) !important',
            '& th, & td': {
              border: '1px solid rgba(0, 0, 0, 0.12) !important',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {tableData.headers.map((header, index) => (
                <TableCell
                  key={index}
                  colSpan={header.colspan}
                  rowSpan={header.rowspan}
                  align="center"
                  sx={{
                    backgroundColor: getBackgroundColor(header.type),
                    color: 'white',
                    fontWeight: 'bold',
                    width: header.width,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    padding: '16px 8px',
                    minHeight: '60px',
                    verticalAlign: 'middle'
                  }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
            {tableData.headers.some((header) => header.subHeaders?.length) && (
            <TableRow>
              {tableData.headers.map((header, index) => (
                header.subHeaders?.map((subHeader, subIndex) => (
                  <TableCell
                    key={`${index}-${subIndex}`}
                    align="center"
                    sx={{
                      backgroundColor: getBackgroundColor(header.type),
                      color: 'white',
                      fontWeight: 'bold',
                      width: subHeader.width,
                      position: 'sticky',
                      top: header.rowspan ? 0 : 56,
                      zIndex: 2,
                      padding: '16px 8px',
                      minHeight: '60px',
                      verticalAlign: 'middle'
                    }}
                  >
                    {subHeader.label}
                  </TableCell>
                ))
              ))}
            </TableRow>
            )}
          </TableHead>
          <TableBody>
            {tableData.rows.map((row, index) => {
              // 각 행의 성적서 첨부 상태 확인
              const hasTestReport = row.hasExactTestReport || 
                                   selectedSubstituteDocuments[row.id] ||
                                   (isProcessingType(row.division) && row.hasPerformanceReport);
              
              return (
                <TableRow 
                  key={row.id || index}
                  sx={{
                    backgroundColor: hasTestReport ? 'rgba(76, 175, 80, 0.05)' : 'transparent',
                    '&:hover': {
                      backgroundColor: hasTestReport ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  {/* 구분 */}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      {hasTestReport && <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 16 }} />}
                      {row.divisionLabel || getDivisionLabel(row.division)}
                    </Box>
                  </TableCell>
                  {/* 업체명 */}
                  <TableCell align="center">
                    {row.company}
                  </TableCell>
                  {/* 제품명 */}
                  <TableCell align="center">
                    <Box sx={{ textAlign: 'left', p: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                        {row.productName}
                      </Typography>
                      {(row.g04docuGCertificationName || row.gstandardNo) && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            display: 'block',
                            mt: 0.5,
                            whiteSpace: 'pre-line'
                          }}
                        >
                          {row.gstandardNo && `[${row.gstandardNo}] `}
                          {row.g04docuGCertificationName || ''}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {/* 원본 성적서 */}
                  <TableCell 
                    align="center"
                    sx={{ verticalAlign: 'middle' }}
                  >
                    {getOriginalDocumentIcon(row)}
                  </TableCell>
                  {/* 대체성적서(현재 선택된 성적서) */}
                  <TableCell 
                    align="center"
                    sx={{ verticalAlign: 'middle' }}
                  >
                    {getCurrentDocumentIcon(row)}
                  </TableCell>
                  {/* 원본 시험성과대비표 */}
                  <TableCell 
                    align="center"
                    sx={{ verticalAlign: 'middle' }}
                  >
                    {getOriginalComparisonIcon(row)}
                  </TableCell>
                  {/* 수정 시험성과대비표 */}
                  <TableCell 
                    align="center"
                    sx={{ verticalAlign: 'middle' }}
                  >
                    {getModifiedComparisonIcon(row)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 문서 뷰어 Drawer */}
      <DocumentViewerDrawer 
        open={documentViewerDrawerOpen}
        onClose={closeDocumentViewer}
        url={currentUrl}
      />

      {/* 대체 문서 선택 팝업 메뉴 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: isComparisonMenu ? '200px' : '350px',
          },
        }}
      >
        {selectedRow && (
          <>
            {console.log('Menu rendering - selectedRow:', selectedRow)}
            {console.log('Menu rendering - division:', selectedRow.division)}
            {isComparisonMenu ? (
              // 대체시험성과대비표 메뉴 (수정본이 있는 경우에만 팝업메뉴 표시)
              <>
                {/* 보기 메뉴 */}
                <MenuItem 
                  onClick={() => {
                    handleOpenComparisonDialog(selectedRow);
                    handleMenuClose();
                  }}
                >
                  <ListItemIcon>
                    <Tooltip title="서류보기" placement="left">
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleComparisonMenuIconClick(selectedRow.gclientId, selectedRow);
                        }}
                        sx={{ color: '#2EA2DE', p: 0 }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText primary="수정" />
                  <Tooltip title="삭제">
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteComparisonReport(selectedRow);
                      }}
                      sx={{ color: 'text.secondary', p: 0 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MenuItem>
              </>
            ) : (
              // 성적서 대체 문서 메뉴
              <>
                {isProcessingType(selectedRow.division) ? (
                  // 가공인 경우
                  <>
                    {/* 성능확인서가 있는 경우 */}
                    {selectedRow.hasPerformanceReport && selectedRow.performanceDocumentPath && (
                      <MenuItem 
                        onClick={() => handleMenuDocumentSelect(selectedRow, { path: selectedRow.performanceDocumentPath, name: '성능확인서' }, 'performance')}
                      >
                        <ListItemIcon sx={{ minWidth: 50, display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }}>
                            {getCurrentSelectedDocument(selectedRow)?.documentPath === selectedRow.performanceDocumentPath && (
                              <CheckIcon fontSize="small" sx={{ color: '#1976d2' }} />
                            )}
                          </Box>
                          <Tooltip title="서류보기" placement="left">
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleMenuIconClick(selectedRow.performanceDocumentPath, selectedRow.gclientId, selectedRow);
                              }}
                              sx={{ color: '#1976d2', p: 0 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </ListItemIcon>
                        <ListItemText 
                          primary="성능확인서" 
                          sx={{ 
                            ml: 1,
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem'
                            }
                          }} 
                        />
                      </MenuItem>
                    )}
                    
                    {/* 현재 선택된 대체성적서가 있는 경우 */}
                    {(() => {
                      const currentSelected = getCurrentSelectedDocument(selectedRow);
                      
                      // 후보군에 있는 문서인지 확인
                      const isInOtherDocuments = selectedRow.otherDocuments && 
                        selectedRow.otherDocuments.some(doc => 
                          doc.path === currentSelected?.documentPath && 
                          doc.gclientId === currentSelected?.gclientId
                        );
                      
                      if (currentSelected && currentSelected.documentPath && 
                          currentSelected.documentType !== 'performance' && 
                          !isInOtherDocuments) {
                        let iconColor = DOC_STATUS_COLOR.substitute;
                        if (currentSelected.isValid === false) iconColor = DOC_STATUS_COLOR.expired;
                        else if (currentSelected.isValid !== true) iconColor = DOC_STATUS_COLOR.missing;
                        return (
                          <MenuItem 
                            onClick={() => handleMenuDocumentSelect(selectedRow, { 
                              path: currentSelected.documentPath, 
                              name: currentSelected.documentName || '대체성적서',
                              gclientId: currentSelected.gclientId,
                              gclient: currentSelected.gclient,
                              isValid: currentSelected.isValid,
                              isValidYN: currentSelected.isValidYN,
                              gtestComparisonDocumentPath: currentSelected.gtestComparisonDocumentPath,
                            }, '대체성적서')}
                          >
                            <ListItemIcon sx={{ minWidth: 50, display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }}>
                                <CheckIcon fontSize="small" sx={{ color: iconColor }} />
                              </Box>
                              <Tooltip title="서류보기" placement="left">
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleMenuIconClick(currentSelected.documentPath, selectedRow.gclientId, selectedRow);
                                  }}
                                  sx={{ color: iconColor, p: 0 }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemIcon>
                            <ListItemText 
                              primary={currentSelected.documentName || '대체성적서'} 
                              sx={{ 
                                ml: 1,
                                '& .MuiListItemText-primary': {
                                  fontSize: '0.875rem'
                                }
                              }} 
                            />
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleRemoveSubstituteDocument(selectedRow);
                              }}
                              sx={{ color: 'text.secondary', p: 0 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </MenuItem>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* 추가성적서목록 (KCC, 대표명 성적서) */}
                    {selectedRow.otherDocuments && selectedRow.otherDocuments
                      .map((doc, index) => {
                        console.log('추가성적서목록:', doc);
                        const isSelected = getCurrentSelectedDocument(selectedRow)?.documentPath === doc.path;
                        // 유효성 색상 결정 - 표 목록과 동일하게 조정
                        let iconColor = DOC_STATUS_COLOR.substitute;
                        if (doc.isValid === false) iconColor = DOC_STATUS_COLOR.expired;
                        else if (doc.isValid !== true) iconColor = DOC_STATUS_COLOR.missing;
                        return (
                          <MenuItem 
                            key={`other-${index}`} 
                            onClick={() => handleMenuDocumentSelect(
                              selectedRow,
                              {
                                g04docuGTestId: doc.g04docuGTestId,
                                path: doc.path,
                                name: doc.name,
                                gclientId: doc.gclientId,
                                gclient: doc.gclient,
                                isValid: doc.isValid,
                                isValidYN: doc.isValidYN,
                                gtestComparisonDocumentPath: doc.gtestComparisonDocumentPath,
                              },
                              '대체성적서')}
                          >
                            <ListItemIcon sx={{ minWidth: 50, display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }}>
                                {isSelected && <CheckIcon fontSize="small" sx={{ color: iconColor }} />}
                              </Box>
                              <Tooltip title="서류보기" placement="left">
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleMenuIconClick(doc.path, selectedRow.gclientId, selectedRow);
                                  }}
                                  sx={{ color: iconColor, p: 0 }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemIcon>
                            <ListItemText 
                              primary={
                                <MenuItemTextWithCompany name={doc.name} companyName={doc.companyName} />
                              }
                              sx={{ ml: 1 }}
                            />
                            {isSelected && (
                              <IconButton
                                size="small"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleRemoveSubstituteDocument(selectedRow);
                                }}
                                sx={{ color: 'text.secondary', p: 0 }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </MenuItem>
                        );
                      })
                    }
                    
                    {/* 기타 옵션 - 다이얼로그 열기 */}
                    <MenuItem onClick={() => {
                      handleOpenSubstituteDialog(selectedRow);
                      handleMenuClose();
                    }}>
                      <ListItemIcon sx={{ minWidth: 50, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }} />
                        <MoreHorizIcon fontSize="small" sx={{ color: '#1976d2' }} />
                      </ListItemIcon>
                      <ListItemText primary="찾기" sx={{ ml: 1 }} />
                    </MenuItem>
                  </>
                ) : (
                  // 가공 외인 경우
                  <>
                    {/* 현재 선택된 대체성적서가 있는 경우 */}
                    {(() => {
                      const currentSelected = getCurrentSelectedDocument(selectedRow);
                      if (currentSelected && currentSelected.documentPath) {
                        let iconColor = DOC_STATUS_COLOR.substitute;
                        if (currentSelected.isValid === false) iconColor = DOC_STATUS_COLOR.expired;
                        else if (currentSelected.isValid !== true) iconColor = DOC_STATUS_COLOR.missing;
                        return (
                          <MenuItem 
                            onClick={() => handleMenuDocumentSelect(selectedRow, { 
                              path: currentSelected.documentPath, 
                              name: currentSelected.documentName || '대체성적서',
                              gclientId: currentSelected.gclientId,
                              gclient: currentSelected.gclient,
                              isValid: currentSelected.isValid,
                              isValidYN: currentSelected.isValidYN,
                              gtestComparisonDocumentPath: currentSelected.gtestComparisonDocumentPath,
                            }, '대체성적서')}
                          >
                            <ListItemIcon sx={{ minWidth: 50, display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }}>
                                <CheckIcon fontSize="small" sx={{ color: iconColor }} />
                              </Box>
                              <Tooltip title="서류보기" placement="left">
                                <IconButton
                                  size="small"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleMenuIconClick(currentSelected.documentPath, selectedRow.gclientId, selectedRow);
                                  }}
                                  sx={{ color: iconColor, p: 0 }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemIcon>
                            <ListItemText 
                              primary={currentSelected.documentName || '대체성적서'} 
                              sx={{ 
                                ml: 1,
                                '& .MuiListItemText-primary': {
                                  fontSize: '0.875rem'
                                }
                              }} 
                            />
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                handleRemoveSubstituteDocument(selectedRow);
                              }}
                              sx={{ color: 'text.secondary', p: 0 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </MenuItem>
                        );
                      }
                      return null;
                    })()}
                    {/* 기타 옵션 - 다이얼로그 열기 */}
                    <MenuItem onClick={() => {
                      handleOpenSubstituteDialog(selectedRow);
                      handleMenuClose();
                    }}>
                      <ListItemIcon sx={{ minWidth: 50, display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }} />
                        <MoreHorizIcon fontSize="small" sx={{ color: '#1976d2' }} />
                      </ListItemIcon>
                      <ListItemText primary="찾기" sx={{ ml: 1 }} />
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Menu>

      {/* 대체성적서 찾기 다이얼로그 */}
      <SubstituteDocumentDialog
        open={substituteDialogOpen}
        onClose={handleCloseSubstituteDialog}
        row={selectedRowForSubstitute}
        onDocumentSelected={handleSubstituteDocumentSelected}
        gprojectId={gproject?.id}
      />
      <FolderSubstitutePickerDialog
        open={folderSubstituteDialogOpen}
        onClose={handleCloseSubstituteDialog}
        row={selectedRowForSubstitute}
        onDocumentSelected={handleSubstituteDocumentSelected}
        onPreview={handleDocumentClick}
      />

      {/* 시험성과대비표 다이얼로그 */}
      <TestComparisonDialog
        gprojectId={gproject?.id}
        open={comparisonDialogOpen}
        onClose={handleCloseComparisonDialog}
        selectedRow={selectedRowForComparison}
        fullScreen={fullScreen}
        onSaveSuccess={async (selectedRow) => {
          // 저장 성공 후 수정 시험성과대비표 목록 새로고침 & 목차트리 업데이트
          if (onComparisonDocumentUpdated) {
            // 수정 시험성과대비표 문서경로 전달
            const comparisons = await gprojectActions.selectGProjectGTestComparisonsDirect(gproject.id);
            setGprojectGTestComparisons(comparisons);
            const selectedGTestComparisons = comparisons.filter(g => g.g04docuGTestId === selectedRow.g04docuGTestId);
            const updateComparisonDocumentPath = selectedGTestComparisons.length === 1 ? selectedGTestComparisons[0].documentPath : null;
            
            // 원본 시험성과대비표가 없는 경우 플래그와 함께 전달
            const hasOriginal = await hasOriginalComparisonDocument(selectedRow);
            const isModifiedComparisonOnly = !hasOriginal;
            
            onComparisonDocumentUpdated(selectedRow.id, updateComparisonDocumentPath, isModifiedComparisonOnly);
          }
        }}
      />
    </Box>
  );
};

export default TestReportTable; 